import React, { useEffect, useMemo, useState } from 'react'
import {
    InlineNotification,
    Modal,
    TextArea,
} from '@carbon/react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    selectSelectedIngestLocation,
    selectToken,
    submitAvidIngestJobs,
} from '../../../AppSlice';
import { IIngestMetadata } from './Ingest.model';
import IngestTree from './IngestTree';

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmitted: () => void;
};

export default function CreateIngestModal({ open, onClose, onSubmitted }: Props) {
    const dispatch = useAppDispatch()
    const token = useAppSelector(selectToken)
    const selectedResource = useAppSelector(selectSelectedIngestLocation)

    const [selectedFiles, setSelectedFiles] = useState<string[]>([])
    const [metadataText, setMetadataText] = useState('{}')
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')

    useEffect(() => {
        if (!open) return
        setSelectedFiles([])
        setMetadataText('{}')
        setSubmitting(false)
        setSubmitError('')
    }, [open])

    const metadataResult = useMemo((): { value?: IIngestMetadata; error?: string } => {
        try {
            const value = JSON.parse(metadataText || '{}')
            if (!value || Array.isArray(value) || typeof value !== 'object') {
                return { error: 'Metadata must be a JSON object.' }
            }
            return { value }
        } catch (_error) {
            return { error: 'Enter valid JSON metadata.' }
        }
    }, [metadataText])

    const canSubmit = Boolean(
        token
        && selectedResource
        && selectedFiles.length > 0
        && metadataResult.value
        && !submitting
    )

    const handleSubmit = async () => {
        if (!canSubmit || !selectedResource || !metadataResult.value) return

        setSubmitting(true)
        setSubmitError('')
        try {
            await dispatch(submitAvidIngestJobs({
                token,
                resourceId: selectedResource,
                files: selectedFiles,
                metadata: metadataResult.value,
            })).unwrap()
            onSubmitted()
            onClose()
        } catch (error: any) {
            setSubmitError(error?.message ?? 'Could not start the Avid ingest jobs.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            open={open}
            modalHeading="New Avid Ingest"
            primaryButtonText={submitting
                ? 'Submitting...'
                : selectedFiles.length > 0
                    ? `Ingest ${selectedFiles.length} File(s)`
                    : 'Ingest'}
            primaryButtonDisabled={!canSubmit}
            secondaryButtonText="Cancel"
            onRequestClose={onClose}
            onRequestSubmit={handleSubmit}
            preventCloseOnClickOutside
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ margin: 0 }}>
                    Choose files from a source resource. Each selected file creates its own Avid ingest job.
                </p>

                <IngestTree
                    selectedFiles={selectedFiles}
                    onSelectedFilesChange={setSelectedFiles}
                />

                <TextArea
                    id="avid-ingest-metadata"
                    labelText="Metadata (JSON)"
                    helperText="Optional metadata applied to every selected file."
                    rows={6}
                    value={metadataText}
                    invalid={Boolean(metadataResult.error)}
                    invalidText={metadataResult.error}
                    onChange={event => setMetadataText(event.target.value)}
                />

                {submitError && (
                    <InlineNotification
                        kind="error"
                        title="Ingest submission failed"
                        subtitle={submitError}
                        lowContrast
                        onCloseButtonClick={() => setSubmitError('')}
                    />
                )}
            </div>
        </Modal>
    )
}
